import React, { useState, useEffect } from 'react';
import { X, Shield, Loader } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const TwoFactorModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [method, setMethod] = useState('totp'); // 'totp' or 'sms'
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (code?.length === 6) {
      onSubmit(code);
    }
  };

  const handleResendSMS = async () => {
    try {
      setCountdown(60);
      // API call to resend SMS would go here
      console.log('Resending SMS...');
    } catch (error) {
      console.error('Failed to resend SMS:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 rounded-full p-2">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {t('Two-Factor Authentication')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6 text-center">
            {t('Please enter the 6-digit verification code')}
          </p>

          {/* Method Selection */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              type="button"
              onClick={() => setMethod('totp')}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                method === 'totp' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('Authenticator App')}
            </button>
            <button
              type="button"
              onClick={() => setMethod('sms')}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                method === 'sms' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('SMS')}
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                placeholder={t('Enter 6-digit code')}
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                required
              />
            </div>

            {/* Resend SMS Button */}
            {method === 'sms' && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendSMS}
                  disabled={countdown > 0}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {countdown > 0
                    ? `${t('Resend in')} ${countdown}s`
                    : t('Resend SMS Code')
                  }
                </button>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={code?.length !== 6 || isLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    {t('Verifying...')}
                  </>
                ) : (
                  t('Verify')
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;